import React, { useState, useRef, useEffect, useCallback } from 'react';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;
const DRAFT_KEY = 'bobine_audio_draft';

const AudioRecorder = ({ photoId, existingAudio, existingTranscription, token, onAudioSaved, onAudioDeleted }) => {
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [transcription, setTranscription] = useState(existingTranscription || '');
  const [hasAudio, setHasAudio] = useState(!!existingAudio);
  const [elapsed, setElapsed] = useState(0);
  const [playProgress, setPlayProgress] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [micError, setMicError] = useState('');
  const [showDraftPrompt, setShowDraftPrompt] = useState(false);
  const [showFullTranscription, setShowFullTranscription] = useState(false);
  const [waveData, setWaveData] = useState(new Array(32).fill(4));
  // Playback waveform
  const [playWaveData, setPlayWaveData] = useState(new Array(28).fill(4));

  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);
  const playAnimFrameRef = useRef(null);
  const playAudioCtxRef = useRef(null);
  const playAnalyserRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const streamRef = useRef(null);

  const MAX_DURATION = 180;

  // Check for saved draft on mount
  useEffect(() => {
    try {
      const draft = localStorage.getItem(DRAFT_KEY);
      if (draft) {
        const parsed = JSON.parse(draft);
        if (parsed.photoId === photoId && parsed.data) setShowDraftPrompt(true);
      }
    } catch { /* ignore */ }
  }, [photoId]);

  // Load existing audio state
  useEffect(() => {
    setHasAudio(!!existingAudio);
    setTranscription(existingTranscription || '');
    setAudioBlob(null);
    setAudioUrl(null);
    setRecording(false);
    setElapsed(0);
    setPlayProgress(0);
    setPlaying(false);
    setMicError('');
    setShowFullTranscription(false);
    cleanupPlayback();
  }, [photoId, existingAudio, existingTranscription]);

  // Save draft on visibility change
  useEffect(() => {
    const handleVisChange = () => {
      if (document.hidden && recording && chunksRef.current.length > 0) {
        try {
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          const reader = new FileReader();
          reader.onloadend = () => {
            localStorage.setItem(DRAFT_KEY, JSON.stringify({ photoId, data: reader.result, elapsed, timestamp: Date.now() }));
          };
          reader.readAsDataURL(blob);
        } catch { /* ignore */ }
        stopRecording();
      }
    };
    document.addEventListener('visibilitychange', handleVisChange);
    return () => document.removeEventListener('visibilitychange', handleVisChange);
  }, [recording, photoId, elapsed]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (playAnimFrameRef.current) cancelAnimationFrame(playAnimFrameRef.current);
      if (audioContextRef.current) audioContextRef.current.close().catch(() => {});
      if (playAudioCtxRef.current) playAudioCtxRef.current.close().catch(() => {});
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    };
  }, []);

  const cleanupPlayback = () => {
    if (playAnimFrameRef.current) { cancelAnimationFrame(playAnimFrameRef.current); playAnimFrameRef.current = null; }
    if (playAudioCtxRef.current) { playAudioCtxRef.current.close().catch(() => {}); playAudioCtxRef.current = null; }
    playAnalyserRef.current = null;
    sourceNodeRef.current = null;
    setPlayWaveData(new Array(28).fill(4));
  };

  const restoreDraft = () => {
    try {
      const draft = JSON.parse(localStorage.getItem(DRAFT_KEY));
      if (draft?.data) {
        const byteStr = atob(draft.data.split(',')[1]);
        const mime = draft.data.split(',')[0].split(':')[1].split(';')[0];
        const ab = new ArrayBuffer(byteStr.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteStr.length; i++) ia[i] = byteStr.charCodeAt(i);
        const blob = new Blob([ab], { type: mime });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setElapsed(draft.elapsed || 0);
      }
    } catch { /* ignore */ }
    localStorage.removeItem(DRAFT_KEY);
    setShowDraftPrompt(false);
  };

  const discardDraft = () => { localStorage.removeItem(DRAFT_KEY); setShowDraftPrompt(false); };

  // ===== RECORDING =====
  const startRecording = async () => {
    setMicError('');
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setMicError('Votre navigateur ne supporte pas l\'enregistrement audio. Utilisez Chrome, Firefox ou Safari en HTTPS.');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 48000 } });
      streamRef.current = stream;
      if (navigator.vibrate) navigator.vibrate(50);

      // AudioContext + AnalyserNode — resume() for mobile
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      if (audioCtx.state === 'suspended') await audioCtx.resume();
      audioContextRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.5;
      analyser.minDecibels = -90;
      analyser.maxDecibels = -10;
      source.connect(analyser);
      analyserRef.current = analyser;

      let mimeType = 'audio/webm';
      for (const m of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']) {
        if (MediaRecorder.isTypeSupported(m)) { mimeType = m; break; }
      }
      const mr = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setAudioBlob(blob); setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
        if (audioContextRef.current) { audioContextRef.current.close().catch(() => {}); audioContextRef.current = null; }
        if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
        localStorage.removeItem(DRAFT_KEY);
      };
      mr.start(250);
      setRecording(true); setElapsed(0); setAudioBlob(null); setAudioUrl(null);

      timerRef.current = setInterval(() => {
        setElapsed(prev => { if (prev >= MAX_DURATION - 1) { stopRecording(); return MAX_DURATION; } return prev + 1; });
      }, 1000);

      // Waveform animation
      const bufLen = analyser.frequencyBinCount;
      const freqArr = new Uint8Array(bufLen);
      const timeArr = new Uint8Array(bufLen);
      const drawWave = () => {
        if (!analyserRef.current) return;
        analyserRef.current.getByteFrequencyData(freqArr);
        analyserRef.current.getByteTimeDomainData(timeArr);
        const bars = [];
        const step = Math.max(1, Math.floor(bufLen / 32));
        let hasSig = false;
        for (let i = 0; i < 32; i++) { if ((freqArr[i * step] || 0) > 5) { hasSig = true; break; } }
        for (let i = 0; i < 32; i++) {
          if (hasSig) { bars.push(Math.max(4, (freqArr[i * step] / 255) * 38)); }
          else { const a = Math.abs((timeArr[i * step] || 128) - 128) / 128; bars.push(Math.max(4, a * 38)); }
        }
        setWaveData(bars);
        animFrameRef.current = requestAnimationFrame(drawWave);
      };
      drawWave();
    } catch (e) {
      const msgs = {
        NotAllowedError: 'Accès au microphone refusé. Autorisez-le dans les paramètres de votre navigateur.',
        NotFoundError: 'Aucun microphone détecté sur cet appareil.',
        NotReadableError: 'Le microphone est utilisé par une autre application.',
      };
      setMicError(msgs[e.name] || 'Impossible d\'accéder au microphone. Vérifiez HTTPS et les permissions.');
    }
  };

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state !== 'inactive') mediaRecorderRef.current?.stop();
    setRecording(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
  }, []);

  // ===== PLAYBACK with live waveform =====
  // Key: AudioContext + analyser must be created BEFORE play() for mobile
  const setupAndPlay = async (audioSrc, isCrossOrigin) => {
    if (playing && audioRef.current) {
      audioRef.current.pause();
      setPlaying(false);
      cleanupPlayback();
      return;
    }
    cleanupPlayback();

    // 1. Create audio element first
    const audio = new Audio(audioSrc);
    if (isCrossOrigin) audio.crossOrigin = 'anonymous';
    audioRef.current = audio;

    // 2. Try to create AudioContext + analyser (may fail on mobile CORS)
    let analyserConnected = false;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      await ctx.resume();
      playAudioCtxRef.current = ctx;

      const source = ctx.createMediaElementSource(audio);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 128;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      analyser.connect(ctx.destination);
      playAnalyserRef.current = analyser;
      sourceNodeRef.current = source;
      analyserConnected = true;
    } catch (e) {
      console.warn('Analyser setup failed, using simulated waveform:', e);
    }

    // 3. Start animation loop
    if (analyserConnected) {
      // Real analyser data
      const bufLen = playAnalyserRef.current.frequencyBinCount;
      const freqArr = new Uint8Array(bufLen);
      const animatePlayback = () => {
        if (!playAnalyserRef.current) return;
        playAnalyserRef.current.getByteFrequencyData(freqArr);
        const bars = [];
        const step = Math.max(1, Math.floor(bufLen / 28));
        for (let i = 0; i < 28; i++) {
          bars.push(Math.max(4, (freqArr[i * step] / 255) * 26));
        }
        setPlayWaveData(bars);
        playAnimFrameRef.current = requestAnimationFrame(animatePlayback);
      };
      animatePlayback();
    } else {
      // Fallback: simulated gold dust animation when analyser can't connect
      const animateSimulated = () => {
        const bars = [];
        const t = Date.now() / 200;
        for (let i = 0; i < 28; i++) {
          const wave = Math.sin(t + i * 0.4) * 0.5 + 0.5;
          const noise = Math.random() * 0.3;
          bars.push(Math.max(4, (wave + noise) * 22));
        }
        setPlayWaveData(bars);
        playAnimFrameRef.current = requestAnimationFrame(animateSimulated);
      };
      animateSimulated();
    }

    // 4. Progress tracking
    audio.ontimeupdate = () => { if (audio.duration) setPlayProgress((audio.currentTime / audio.duration) * 100); };
    audio.onended = () => { setPlaying(false); setPlayProgress(0); cleanupPlayback(); };

    // 5. Play
    setPlaying(true);
    try { await audio.play(); } catch (e) { console.warn('Play error:', e); setPlaying(false); cleanupPlayback(); }
  };

  const playExisting = () => setupAndPlay(`${API}/photos/${photoId}/audio?token=${token}`, true);

  const playPreview = () => {
    if (!audioUrl) return;
    setupAndPlay(audioUrl, false);
  };

  const saveAudio = async () => {
    if (!audioBlob) return;
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('file', audioBlob, `recit-${photoId}.webm`);
      const res = await fetch(`${API}/photos/${photoId}/audio`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      if (!res.ok) throw new Error('Erreur sauvegarde');
      const data = await res.json();
      setTranscription(data.transcription || '');
      setHasAudio(true); setAudioBlob(null);
      localStorage.removeItem(DRAFT_KEY);
      if (onAudioSaved) onAudioSaved(photoId, data.audio_filename, data.transcription);
    } catch (e) { console.error('Save audio error:', e); }
    setSaving(false);
  };

  const deleteAudio = async () => {
    try {
      await fetch(`${API}/photos/${photoId}/audio`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setHasAudio(false); setTranscription(''); setShowDeleteConfirm(false);
      if (onAudioDeleted) onAudioDeleted(photoId);
    } catch (e) { console.error('Delete error:', e); }
  };

  const discard = () => { setAudioBlob(null); setAudioUrl(null); setElapsed(0); setPlayProgress(0); cleanupPlayback(); if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } };
  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  // ===== Transcription truncation helper =====
  const isLongTranscription = transcription && transcription.length > 180;
  const displayedTranscription = (!showFullTranscription && isLongTranscription)
    ? transcription.slice(0, 180).replace(/\s+\S*$/, '') + '...'
    : transcription;

  // ====== RENDER ======

  // Draft recovery prompt
  if (showDraftPrompt) {
    return (
      <div data-testid="audio-draft-prompt" onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 500, margin: '16px auto 0', textAlign: 'center', padding: '16px 20px', borderRadius: 10, border: '1px solid rgba(212,175,55,0.3)', backgroundColor: 'rgba(212,175,55,0.04)' }}>
        <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37', marginBottom: 12 }}>Un récit interrompu a été retrouvé. Voulez-vous le terminer ?</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={discardDraft} data-testid="draft-discard-btn" style={{ fontFamily: "'Georgia', serif", fontSize: 12, padding: '8px 18px', borderRadius: 6, border: '1px solid rgba(212,175,55,0.3)', backgroundColor: 'transparent', color: '#D4AF37', cursor: 'pointer' }}>Ignorer</button>
          <button onClick={restoreDraft} data-testid="draft-restore-btn" style={{ fontFamily: "'Georgia', serif", fontSize: 12, padding: '8px 18px', borderRadius: 6, border: 'none', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', cursor: 'pointer', fontWeight: 600 }}>Reprendre le récit</button>
        </div>
      </div>
    );
  }

  // Saved audio — LIVE waveform + transcription behind Plume button
  if (hasAudio && !audioBlob) {
    return (
      <div data-testid="audio-player-section" onClick={e => e.stopPropagation()} data-no-close style={{ width: '100%', maxWidth: 500, margin: '16px auto 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderRadius: 10, border: '1px solid rgba(212,175,55,0.2)', backgroundColor: 'rgba(212,175,55,0.04)' }}>
          <button onClick={playExisting} data-testid="audio-play-btn"
            style={{ width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s', flexShrink: 0 }}>
            {playing ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#000"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#000"><polygon points="5,3 19,12 5,21"/></svg>
            )}
          </button>
          {/* Live animated waveform during playback */}
          <div data-testid="audio-playback-waveform" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, height: 28 }}>
            {playWaveData.map((h, i) => {
              const baseH = 6 + Math.sin(i * 0.7) * 10 + Math.cos(i * 1.3) * 6;
              const barH = playing ? Math.max(baseH * 0.3, h) : baseH;
              const filled = (i / 28) * 100 <= playProgress;
              return (
                <div key={i} style={{
                  width: 3, borderRadius: 2, flexShrink: 0,
                  height: Math.max(4, barH),
                  backgroundColor: filled ? '#D4AF37' : 'rgba(212,175,55,0.2)',
                  transition: playing ? 'height 0.08s linear, background-color 0.15s' : 'background-color 0.15s',
                  boxShadow: playing && h > 10 ? '0 0 3px rgba(212,175,55,0.3)' : 'none',
                }} />
              );
            })}
          </div>
          {/* Stop button — visible only when playing */}
          {playing && (
            <button onClick={() => { if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; } setPlaying(false); setPlayProgress(0); cleanupPlayback(); }} data-testid="audio-stop-btn"
              style={{ width: 30, height: 30, borderRadius: '50%', border: '1px solid rgba(212,175,55,0.4)', backgroundColor: 'rgba(212,175,55,0.08)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.3s' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="#D4AF37"><rect x="4" y="4" width="16" height="16" rx="2"/></svg>
            </button>
          )}
          <button onClick={() => setShowDeleteConfirm(true)} data-testid="audio-delete-btn"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#D4AF37', transition: 'color 0.3s', flexShrink: 0 }}
            onMouseEnter={e => { e.currentTarget.style.color = '#c0392b'; }}
            onMouseLeave={e => { e.currentTarget.style.color = '#D4AF37'; }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
          </button>
        </div>

        {/* Plume button — toggles transcription */}
        {transcription && (
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <button
              onClick={() => setShowFullTranscription(!showFullTranscription)}
              data-testid="transcription-plume-btn"
              style={{
                fontFamily: "'Georgia', serif", fontSize: 11,
                color: '#D4AF37', background: '#D4AF37',
                border: '1px solid rgba(212,175,55,0.25)',
                borderRadius: 16, padding: '5px 16px', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                transition: 'all 0.3s',
              }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(212,175,55,0.12)'; e.currentTarget.style.borderColor = '#D4AF37'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(212,175,55,0.06)'; e.currentTarget.style.borderColor = 'rgba(212,175,55,0.25)'; }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.24 12.24a6 6 0 00-8.49-8.49L5 10.5V19h8.5z"/>
                <line x1="16" y1="8" x2="2" y2="22"/><line x1="17.5" y1="15" x2="9" y2="15"/>
              </svg>
              {showFullTranscription ? 'Masquer le récit' : 'Lire le récit'}
            </button>
            {showFullTranscription && (
              <div data-testid="audio-transcription" style={{ marginTop: 8, padding: '10px 14px', borderRadius: 8, backgroundColor: 'rgba(212,175,55,0.03)', border: '1px solid rgba(212,175,55,0.1)' }}>
                <p style={{
                  fontFamily: "'Georgia', serif", fontSize: 13, color: '#FAF3E0',
                  fontStyle: 'italic', lineHeight: 1.6, margin: 0,
                }}>"{transcription}"</p>
                {/* Boutons de transcription 4 langues */}
                <div data-testid="transcription-lang-buttons" style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 10 }}>
                  {[
                    { code: 'fr', label: 'FR' },
                    { code: 'en', label: 'EN' },
                    { code: 'es', label: 'ES' },
                    { code: 'pt', label: 'PT' },
                  ].map(lang => (
                    <button
                      key={lang.code}
                      data-testid={`transcribe-${lang.code}`}
                      onClick={async (e) => {
                        e.stopPropagation();
                        const btn = e.currentTarget;
                        btn.textContent = '...';
                        btn.disabled = true;
                        try {
                          const API = (process.env.REACT_APP_BACKEND_URL || 'https://bobine.live');
                          const tk = localStorage.getItem('bobine_token');
                          const res = await fetch(`${API}/api/photos/${photoId}/retranscribe`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
                            body: JSON.stringify({ language: lang.code }),
                          });
                          const data = await res.json();
                          if (data.status === 'success' && data.transcription) {
                            onAudioSaved && onAudioSaved(photoId, null, data.transcription);
                          }
                        } catch { /* silent */ }
                        btn.textContent = lang.label;
                        btn.disabled = false;
                      }}
                      style={{
                        fontFamily: "'Georgia', serif", fontSize: 10, fontWeight: 600,
                        padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
                        border: '1px solid rgba(212,175,55,0.3)',
                        backgroundColor: 'rgba(212,175,55,0.06)', color: '#D4AF37',
                        letterSpacing: 0.5, transition: 'all 0.2s',
                      }}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 9, color: '#D4AF37', marginTop: 6, fontStyle: 'italic' }}>
                  Choisir la langue de transcription
                </p>
              </div>
            )}
          </div>
        )}

        {showDeleteConfirm && (
          <div style={{ marginTop: 10, padding: '12px 16px', borderRadius: 8, border: '1px solid rgba(192,57,43,0.3)', backgroundColor: 'rgba(192,57,43,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: '#FAF3E0' }}>Supprimer ce récit vocal ?</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowDeleteConfirm(false)} data-testid="audio-delete-cancel" style={{ fontFamily: "'Georgia', serif", fontSize: 11, padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(212,175,55,0.3)', backgroundColor: 'transparent', color: '#D4AF37', cursor: 'pointer' }}>Annuler</button>
              <button onClick={deleteAudio} data-testid="audio-delete-confirm" style={{ fontFamily: "'Georgia', serif", fontSize: 11, padding: '6px 14px', borderRadius: 6, border: 'none', backgroundColor: '#c0392b', color: '#fff', cursor: 'pointer' }}>Supprimer</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Recording in progress — LIVE WAVEFORM
  if (recording) {
    return (
      <div data-testid="audio-recording-section" onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 500, margin: '16px auto 0', textAlign: 'center' }}>
        <div data-testid="audio-live-waveform" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, height: 44, marginBottom: 12 }}>
          {waveData.map((h, i) => (
            <div key={i} style={{ width: 3, borderRadius: 2, height: h, background: 'linear-gradient(145deg, #D4AF37, #B8860B)', transition: 'height 0.06s linear', opacity: 0.5 + (h / 38) * 0.5, boxShadow: h > 15 ? '0 0 4px rgba(212,175,55,0.4)' : 'none' }} />
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 14 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#c0392b', animation: 'bobine-pulse 1.2s ease-in-out infinite' }} />
          <span style={{ fontFamily: "'Georgia', serif", fontSize: 16, color: '#D4AF37', fontVariantNumeric: 'tabular-nums' }}>{formatTime(elapsed)} / {formatTime(MAX_DURATION)}</span>
        </div>
        <button onClick={stopRecording} data-testid="audio-stop-btn" style={{ fontFamily: "'Georgia', serif", fontSize: 14, padding: '10px 28px', borderRadius: 8, background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none', cursor: 'pointer', fontWeight: 600, transition: 'all 0.3s' }}>Sceller le récit</button>
        <style>{`@keyframes bobine-pulse { 0%,100% { opacity:1; } 50% { opacity:0.3; } }`}</style>
      </div>
    );
  }

  // Preview before saving
  if (audioBlob && audioUrl) {
    return (
      <div data-testid="audio-preview-section" onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 500, margin: '16px auto 0', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '10px 16px', borderRadius: 10, border: '1px solid rgba(212,175,55,0.2)', backgroundColor: 'rgba(212,175,55,0.04)', marginBottom: 12 }}>
          <button onClick={playPreview} data-testid="audio-preview-play" style={{ width: 36, height: 36, borderRadius: '50%', border: 'none', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            {playing ? <svg width="14" height="14" viewBox="0 0 24 24" fill="#000"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
              : <svg width="14" height="14" viewBox="0 0 24 24" fill="#000"><polygon points="5,3 19,12 5,21"/></svg>}
          </button>
          <span style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37' }}>Aperçu — {formatTime(elapsed)}</span>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, height: 24 }}>
            {playWaveData.slice(0, 20).map((h, i) => {
              const baseH = 4 + Math.sin(i * 0.8) * 8 + Math.cos(i * 1.5) * 5;
              const barH = playing ? Math.max(baseH * 0.3, h) : baseH;
              const filled = (i / 20) * 100 <= playProgress;
              return <div key={i} style={{ width: 3, borderRadius: 2, height: Math.max(3, barH), backgroundColor: filled ? '#D4AF37' : 'rgba(212,175,55,0.2)', transition: playing ? 'height 0.08s linear, background-color 0.15s' : 'background-color 0.15s' }} />;
            })}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={discard} data-testid="audio-discard-btn" style={{ fontFamily: "'Georgia', serif", fontSize: 13, padding: '10px 22px', borderRadius: 8, border: '1px solid rgba(212,175,55,0.3)', backgroundColor: 'transparent', color: '#D4AF37', cursor: 'pointer' }}>Recommencer</button>
          <button onClick={saveAudio} disabled={saving} data-testid="audio-save-btn" style={{ fontFamily: "'Georgia', serif", fontSize: 13, padding: '10px 22px', borderRadius: 8, border: 'none', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', cursor: 'pointer', fontWeight: 600, opacity: saving ? 0.6 : 1, boxShadow: '0 2px 12px rgba(212,175,55,0.25)' }}>{saving ? 'Scellement en cours...' : 'Sceller ce récit'}</button>
        </div>
      </div>
    );
  }

  // Default — Record button
  return (
    <div onClick={e => e.stopPropagation()} style={{ textAlign: 'center', marginTop: 12 }}>
      <button onClick={startRecording} data-testid="audio-record-btn"
        style={{ fontFamily: "'Georgia', serif", fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 22px', borderRadius: 22, backgroundColor: 'rgba(10,10,10,0.7)', border: '1px solid rgba(212,175,55,0.4)', color: '#D4AF37', cursor: 'pointer', transition: 'all 0.3s' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = '#D4AF37'; e.currentTarget.style.backgroundColor = 'rgba(10,10,10,0.7)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = '#D4AF37'; e.currentTarget.style.backgroundColor = 'rgba(10,10,10,0.7)'; }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
        </svg>
        Confier un récit vocal
      </button>
      {micError && (
        <p data-testid="audio-mic-error" style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: '#c0392b', marginTop: 10, lineHeight: 1.5, padding: '8px 14px', borderRadius: 6, backgroundColor: 'rgba(192,57,43,0.06)', border: '1px solid rgba(192,57,43,0.2)' }}>{micError}</p>
      )}
    </div>
  );
};

export default AudioRecorder;
